import { MessageSquare, PanelLeftClose, Plus, Search, X } from "lucide-react";
import { useMemo, useState, useSyncExternalStore, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { workflowDisplayText } from "@/features/caseIde/workflows/catalog";
import { useRunningTurnsStore } from "@/features/agents/runningTurnsStore";

import type { CaseAgentThread } from "../api/caseAgentApi";
import {
  conversationKind,
  groupConversations,
  needsYou,
  rowTimeLabel,
  splitWorkflowTitle
} from "../conversationGroups";
import { plainMentions } from "../mentions";
import { ConversationRow } from "./ConversationRow";

/* Whether the maximized Casey's sidebar is open, remembered in localStorage. */

const SIDEBAR_KEY = "caseIde.caseySidebarOpen";
const sidebarListeners = new Set<() => void>();

const readSidebarOpen = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) !== "0";
  } catch {
    return true;
  }
};

export const setSidebarOpen = (open: boolean) => {
  try {
    if (open) localStorage.removeItem(SIDEBAR_KEY);
    else localStorage.setItem(SIDEBAR_KEY, "0");
  } catch {
    // Storage unavailable: the sidebar still toggles for this session.
  }
  sidebarListeners.forEach((l) => l());
};

export const useSidebarOpen = (): boolean =>
  useSyncExternalStore(
    (l) => {
      sidebarListeners.add(l);
      return () => {
        sidebarListeners.delete(l);
      };
    },
    readSidebarOpen,
    () => true
  );

type Props = {
  threads: CaseAgentThread[];
  activeId: string | null;
  /** The case's workflow thread, marked as such. */
  workflowThreadId: string | null;
  unreadByThread?: Record<string, number>;
  disabled?: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
};

const titleOf = (t: CaseAgentThread) =>
  plainMentions(workflowDisplayText(t.title)) || "New Chat";

/**
 * The maximized Casey's conversation list, chat-app style: New Chat, a
 * search over titles and last messages, then the case's threads in recency
 * groups. Rows carry a kind icon (sparkle for workflow threads, zap for
 * automation runs), a quiet time line, and a mark when one waits on the user
 * or Casey is working in it.
 */
export const ConversationsSidebar: FC<Props> = ({
  threads,
  activeId,
  workflowThreadId,
  unreadByThread = {},
  disabled,
  onSelect,
  onNew
}) => {
  const [query, setQuery] = useState("");
  const groups = useMemo(
    () => groupConversations(threads, query),
    [threads, query]
  );
  const running = useRunningTurnsStore((s) => s.turns);
  const isRunning = (t: CaseAgentThread) =>
    Boolean(t.active_turn) || running.some((r) => r.threadId === t.id);
  const searching = query.trim().length > 0;

  return (
    <nav
      aria-label="Conversations"
      data-testid="casey-sidebar"
      className="flex h-full w-64 shrink-0 flex-col border-r border-line-2 bg-chrome"
    >
      <div className="flex shrink-0 items-center gap-1.5 px-3 pt-3 pb-2">
        <Button
          variant="secondary"
          size="md"
          className="min-w-0 flex-1 justify-start"
          onClick={onNew}
          disabled={disabled}
          data-testid="casey-sidebar-new"
        >
          <Plus />
          New Chat
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-fg-3 hover:text-fg"
              aria-label="Hide Conversations"
              onClick={() => setSidebarOpen(false)}
            >
              <PanelLeftClose className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Hide Conversations</TooltipContent>
        </Tooltip>
      </div>
      <div className="shrink-0 px-3 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-4" />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations"
            aria-label="Search conversations"
            className="h-8 rounded-lg pr-8 pl-8 [&::-webkit-search-cancel-button]:appearance-none"
          />
          {searching ? (
            <button
              type="button"
              aria-label="Clear Search"
              className="absolute top-1/2 right-1.5 flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-fg-3 outline-none hover:bg-fill-2 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40"
              onClick={() => setQuery("")}
            >
              <X className="size-3" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 [scrollbar-width:thin]">
        {groups.length === 0 ? (
          <div
            className="flex flex-col items-center gap-1 px-3 py-8 text-center"
            data-testid="casey-sidebar-empty"
          >
            <span className="mb-1 flex size-8 items-center justify-center rounded-lg bg-fill-1 text-fg-3">
              {searching ? (
                <Search className="size-4" />
              ) : (
                <MessageSquare className="size-4" />
              )}
            </span>
            <p className="text-base font-medium text-fg">
              {searching ? "No Matches" : "No Conversations Yet"}
            </p>
            <p className="text-sm text-balance text-fg-3">
              {searching
                ? "Try a different word."
                : "Start a new chat to ask Casey anything."}
            </p>
          </div>
        ) : null}
        {groups.map((g) => (
          <div
            key={g.id}
            role="group"
            aria-label={g.label}
            className="flex flex-col gap-0.5 pt-4 first:pt-1"
          >
            <div className="px-2 pb-1 text-sm font-medium text-fg-4">
              {g.label}
            </div>
            {g.threads.map((t) => {
              const kind = conversationKind(t, workflowThreadId);
              const fullTitle = titleOf(t);
              const split = splitWorkflowTitle(fullTitle);
              return (
                <ConversationRow
                  key={t.id}
                  kind={kind}
                  title={split.title}
                  fullTitle={fullTitle}
                  subtitle={split.subtitle}
                  time={rowTimeLabel(t.updated_at ?? t.created_at)}
                  active={t.id === activeId}
                  waiting={needsYou(t)}
                  waitingLabel={
                    t.pending_approval
                      ? "Awaiting approval"
                      : "Waiting on your answer"
                  }
                  running={isRunning(t)}
                  unread={unreadByThread[t.id] ?? 0}
                  onSelect={() => onSelect(t.id)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </nav>
  );
};
